import {
  useState,
  useRef,
  useId,
  useEffect,
  useLayoutEffect,
  type FormEvent,
  type ChangeEvent,
} from "react";
import type { ForumComposePost } from "../forum.types";
export function usePostComposer(compose: ForumComposePost) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [format, setFormat] = useState<"markdown" | "text">("markdown");
  const [image, setImage] = useState<Readonly<{
    file: File;
    previewUrl: string;
  }> | null>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    return () => {
      if (image) URL.revokeObjectURL(image.previewUrl);
    };
  }, [image]);
  const [submitting, setSubmitting] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const fieldId = useId();
  const busy = compose.busy || submitting;
  const dirty =
    title !== "" || body !== "" || image !== null || format !== "markdown";
  const { onDirtyChange } = compose;
  useLayoutEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useLayoutEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (busy || !title.trim() || !body.trim()) return;

    setSubmitting(true);
    const accepted = await compose.onSubmit({
      title,
      body,
      format,
      image: image?.file ?? null,
    });
    setSubmitting(false);

    if (accepted) {
      discard();
    }
  }
  function changeImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setImage(file ? { file, previewUrl: URL.createObjectURL(file) } : null);
  }
  function removeImage() {
    setImage(null);

    if (imageInput.current) imageInput.current.value = "";
  }
  function discard() {
    setTitle("");
    setBody("");
    setImage(null);
    setFormat("markdown");
    setOpen(false);
    trigger.current?.focus();
  }
  return {
    open,
    setOpen,
    title,
    setTitle,
    body,
    setBody,
    format,
    setFormat,
    image,
    imageInput,
    trigger,
    fieldId,
    busy,
    submit,
    changeImage,
    removeImage,
    discard,
  };
}
